import { ETrialIndustrySets, type TrialResearchPreview } from '../types';

/** POST /v3/create-trial Joi max; keep in sync with node-api TrialValidator. */
export const MAX_TRIAL_INDUSTRY_CHAR_LENGTH = 80;

/** While typing: keep trailing spaces; only collapse runs of spaces and enforce max length. */
export function clampTrialIndustryInput(text: string): string {
  if (!text) {
    return '';
  }

  return text.replace(/\s+/g, ' ').slice(0, MAX_TRIAL_INDUSTRY_CHAR_LENGTH);
}

/** Trim, collapse spaces, cap length — use on submit and API payloads, not on every keystroke. */
export function normaliseTrialIndustryLabel(
  text: string | null | undefined,
): string {
  if (!text?.trim()) {
    return '';
  }

  return text.trim().replace(/\s+/g, ' ').slice(0, MAX_TRIAL_INDUSTRY_CHAR_LENGTH);
}

export function isTrialIndustryInputValid(value: string): boolean {
  const trimmed = value.trim();

  return trimmed.length > 0 && trimmed.length <= MAX_TRIAL_INDUSTRY_CHAR_LENGTH;
}

export function trialIndustryInputError(
  value: string,
  translate: (key: string, opts?: Record<string, unknown>) => string,
): string | boolean {
  const trimmed = value.trim();

  if (!trimmed) return true;

  if (trimmed.length > MAX_TRIAL_INDUSTRY_CHAR_LENGTH) {
    return translate('authentication:trial_preview_industry_too_long', {
      max: MAX_TRIAL_INDUSTRY_CHAR_LENGTH,
    });
  }

  return false;
}

export function isKnownTrialIndustrySet(value: string): value is ETrialIndustrySets {
  return (Object.values(ETrialIndustrySets) as string[]).includes(value);
}

/** Prefill for the editable industry field — prefer API `industry_label`, else preset i18n / raw proposal. */
export function industryLabelFromPreview(
  preview: Pick<TrialResearchPreview, 'industry_label' | 'industry_proposed'>,
  translate: (key: string) => string,
): string {
  const fromResearch = normaliseTrialIndustryLabel(preview.industry_label);

  if (fromResearch) {
    return fromResearch;
  }

  const proposed = preview.industry_proposed?.trim();

  if (!proposed) {
    return translate('authentication:trial_industry_set_base');
  }

  if (isKnownTrialIndustrySet(proposed)) {
    return translate(`authentication:trial_industry_set_${proposed}`);
  }

  return proposed;
}

/** POST /v3/create-trial — visitor-editable line; preset mapping happens on the API when `industry_set` is omitted. */
export function industryForCreateTrialPayload(industryLabel: string): string {
  return normaliseTrialIndustryLabel(industryLabel);
}
